"use client";

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SectionHeading from "@/components/SectionHeading";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const testimonials = [
  {
    quote:
      "Within eight weeks, Rehman replaced our manual order-entry team with an AI agent that now processes 2,500 orders per day—cutting labour costs by 60 % and eliminating 99 % of data errors.",
    author: "Maria Chen",
    title: "VP Operations",
    company: "QuickCart Logistics",
  },
  {
    quote:
      "Our customer-support backlog dropped from 1,200 tickets to zero overnight. Rehman' agent pipeline resolves standard inquiries automatically and flags only the 5 % of edge cases for human review.",
    author: "Javier Reyes",
    title: "Head of Customer Experience",
    company: "CloudServe Inc.",
  },
  {
    quote:
      "We trimmed our monthly invoice-processing bill from $18,000 to $4,500. The 30-day pilot paid for itself in just three weeks—and we rolled it out to three more divisions.",
    author: "Linda Okafor",
    title: "Finance Director",
    company: "GlobalPack Co.",
  },
  {
    quote:
      "By automating our recruitment screening, we went from 10 days turnaround to under 3 hours. The accuracy of candidate matching improved 75 %—and our HR team can now focus on human-touch interviews.",
    author: "Omar El-Sayed",
    title: "Chief People Officer",
    company: "TalentBridge",
  },
  {
    quote:
      "Rehman built a generative-AI content engine that drafts 50 blog posts per month. Our content output is up 5×, engagement is up 120 %, and we've saved over 200 manpower hours quarterly.",
    author: "Sophie Kumar",
    title: "Marketing Lead",
    company: "BrightMedia Group",
  },
];

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(true);

  // Enhanced slide variants with smooth overlapping transitions
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 200 : -200,
      opacity: 0,
      scale: 0.98,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 200 : -200,
      opacity: 0,
      scale: 0.98,
    }),
  };

  // Memoized paginate function
  const paginate = useCallback((newDirection: number) => {
    setDirection(newDirection);
    setCurrentIndex(
      (prevIndex) =>
        (prevIndex + newDirection + testimonials.length) % testimonials.length
    );
    // Pause auto-rotation when user interacts
    setIsAutoRotating(false);
    // Resume auto-rotation after 10 seconds
    setTimeout(() => setIsAutoRotating(true), 10000);
  }, []);

  // Optimized auto-rotate with pause on interaction
  useEffect(() => {
    if (!isAutoRotating) return;

    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    }, 6000); // Increased to 6 seconds for better UX

    return () => clearInterval(timer);
  }, [isAutoRotating]);

  // Handle direct navigation
  const handleDotClick = useCallback(
    (index: number) => {
      if (index === currentIndex) return;
      setDirection(index > currentIndex ? 1 : -1);
      setCurrentIndex(index);
      setIsAutoRotating(false);
      setTimeout(() => setIsAutoRotating(true), 10000);
    },
    [currentIndex]
  );

  return (
    <section
      className="py-24 px-6 bg-gradient-to-b from-secondary/30 to-secondary/10"
      onMouseEnter={() => setIsAutoRotating(false)}
      onMouseLeave={() => setIsAutoRotating(true)}
    >
      <div className="container max-w-6xl mx-auto">
        <SectionHeading title="Client Testimonials" />

        <div className="relative flex justify-center min-h-[300px] my-12">
          <div className="max-w-3xl w-full">
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  duration: 0.5,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
                className="absolute w-full"
              >
                <Card className="border-2 border-border/50 bg-card/90 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="pt-8 px-8 pb-6">
                    <div className="absolute -top-5 left-8 bg-accent/10 p-3 rounded-full border-2 border-accent/20">
                      <svg
                        className="w-6 h-6 text-accent"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                      </svg>
                    </div>
                    <p className="text-xl leading-relaxed italic mb-8 text-foreground/90">
                      &ldquo;{testimonials[currentIndex].quote}&rdquo;
                    </p>
                    <div className="flex items-center justify-between gap-4 border-t border-border/50 pt-6">
                      <div>
                        <p className="font-semibold text-lg">
                          {testimonials[currentIndex].author}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {testimonials[currentIndex].title} •{" "}
                          <span className="text-accent/80">
                            {testimonials[currentIndex].company}
                          </span>
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => paginate(-1)}
                          className="hover:bg-accent/20 transition-colors duration-200"
                          aria-label="Previous testimonial"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => paginate(1)}
                          className="hover:bg-accent/20 transition-colors duration-200"
                          aria-label="Next testimonial"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Testimonial Navigation Dots */}
        <div className="flex justify-center gap-3 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`h-2 rounded-full transition-all duration-200 ${
                index === currentIndex
                  ? "bg-accent w-8"
                  : "bg-accent/30 w-2 hover:bg-accent/50"
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
